<!-- src/views/documents/PublishedDocumentsView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto space-y-10 animate-fadeIn">
        
        <!-- 1. MASTER COMMAND HEADER CARD -->
        <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-12 border-b-4 border-[#20FCF1] shadow-2xl relative overflow-hidden border-x border-t border-slate-800">
          <!-- Ambient Background Shield Protocol -->
          <div class="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
            <svg class="h-32 w-32 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L3 7V12C3 17.5 7.1 21.6 12 23C16.9 21.6 21 17.5 21 12V7L12 2Z" fill="currentColor" fill-opacity="0.1" stroke="currentColor" stroke-width="1.5"/>
            </svg>
          </div>

          <div class="relative z-10 space-y-8">
            <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div>
                <h1 class="text-3xl md:text-4xl font-black text-white uppercase tracking-tight leading-none text-left">Document Command Centre</h1>
                <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-4 text-left">
                  Company Policies, Procedures & Governance Registries
                </p>
              </div>
              <router-link 
                to="/dashboard" 
                class="px-6 py-3 bg-white/5 border border-white/10 hover:border-[#20FCF1] hover:bg-[#20FCF1]/10 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg active:scale-95 flex items-center gap-2"
              >
                <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return to Dashboard</span>
              </router-link>
            </div>

            <!-- INTEGRATED SEARCH & DYNAMIC FILTER BAR -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white/5 p-5 rounded-[32px] border border-white/10 text-left">
              <div class="flex flex-col gap-1.5">
                <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Search Registry</span>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon class="h-4 w-4 text-slate-400" />
                  </div>
                  <input 
                    type="text" 
                    v-model="searchQuery" 
                    placeholder="Search documents by title or reference..." 
                    class="filter-input-dark pl-11"
                  />
                </div>
              </div>

              <div class="flex flex-col gap-1.5">
                <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Document Category Filter</span>
                <div class="relative">
                  <select v-model="selectedCategory" class="filter-select-dark">
                    <option value="all">All Categories</option>
                    <option v-for="cat in availableCategories" :key="cat" :value="cat">
                      {{ cat }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 2. MAIN REGISTRY BODY -->
        <div class="space-y-12">
          
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-32 bg-white rounded-[40px] border border-slate-100 shadow-sm">
            <div class="animate-spin h-10 w-10 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full mx-auto mb-4"></div>
            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Hydrating Document Registries...</p>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="bg-rose-50 border border-rose-200 p-8 rounded-[32px] text-center text-[#EF4444] font-black uppercase tracking-widest text-xs">
            {{ error }}
          </div>

          <!-- Empty State -->
          <div v-else-if="Object.keys(groupedDocuments).length === 0" class="text-center py-32 bg-white rounded-[40px] border-2 border-dashed border-slate-200 shadow-sm animate-fadeIn">
            <ClipboardDocumentListIcon class="mx-auto h-16 w-16 text-slate-200 mb-6" />
            <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">Registry Neutral</h3>
            <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">
              {{ searchQuery || selectedCategory !== 'all' ? 'No records match your filters.' : 'There are currently no company documents available.' }}
            </p>
          </div>

          <!-- PREMIUM CARDS GRID -->
          <div v-else class="space-y-16 animate-fadeIn">
            <section v-for="(docs, category) in groupedDocuments" :key="category" class="space-y-6">
              <!-- Category Separator -->
              <div class="flex items-center gap-4 border-b border-slate-100 pb-3">
                <FolderIcon class="h-5 w-5 text-[#20FCF1] shrink-0" />
                <h2 class="text-[11px] font-black text-[#0A1630] uppercase tracking-[0.3em] capitalize">{{ category }}</h2>
              </div>

              <!-- Symmetrical Document Cards -->
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div 
                  v-for="doc in docs" 
                  :key="doc.id"
                  class="group relative bg-white rounded-[32px] p-8 border border-slate-100 shadow-md hover:shadow-2xl transition-all duration-500 flex flex-col justify-between overflow-hidden"
                >
                  <!-- STATUS ANCHOR LINE -->
                  <div class="absolute top-0 left-0 w-full h-1.5 bg-slate-100 group-hover:bg-[#20FCF1] transition-colors duration-500"></div>

                  <div class="relative z-10 space-y-6">
                    <div class="flex justify-between items-start">
                      <!-- Icon Shield -->
                      <div class="relative w-14 h-14 flex items-center justify-center">
                        <div class="absolute inset-0 rounded-full blur-2xl opacity-10 group-hover:opacity-35 transition-opacity duration-700 bg-[#20FCF1]"></div>
                        <svg class="absolute inset-0 w-full h-full drop-shadow-sm" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L3 7V12C3 17.5 7.1 21.6 12 23C16.9 21.6 21 17.5 21 12V7L12 2Z" fill="#20FCF1" fill-opacity="0.05" stroke="#E2E8F0" stroke-width="1.5"/>
                        </svg>
                        <DocumentTextIcon class="w-6 h-6 relative z-10 text-slate-400 group-hover:text-[#FD5E00] transition-colors" />
                      </div>
                      
                      <!-- Dynamic Custom Document Reference Badge -->
                      <span v-if="doc.reference" class="text-[9px] font-black uppercase tracking-wider bg-slate-50 text-slate-500 px-3 py-1.5 rounded-xl border border-slate-100 group-hover:border-[#20FCF1]/20 group-hover:bg-[#20FCF1]/5 transition-all">
                        {{ doc.reference }}
                      </span>
                    </div>

                    <div class="space-y-2 text-left">
                      <h3 class="text-lg font-black text-[#0A1630] uppercase tracking-tight leading-tight group-hover:text-[#FD5E00] transition-colors duration-300">
                        {{ doc.title }}
                      </h3>
                      <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                        <ShieldCheckIcon class="h-4 w-4 text-[#20FCF1]" /> Verified on {{ formatDate(doc.signedAt) }}
                      </p>
                    </div>
                  </div>

                  <div class="relative z-10 pt-8 mt-6 border-t border-slate-50 flex items-center justify-between gap-4">
                    <span class="text-[8px] font-black text-slate-300 uppercase tracking-widest leading-none">Access Level: Operative</span>
                    <router-link 
                      :to="{ name: 'ViewDocument', params: { documentId: doc.id } }"
                      class="px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest bg-[#0A1630] text-white hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-md active:scale-95 flex items-center gap-2"
                    >
                      <span>Initiate Review</span>
                      <ChevronRightIcon class="h-3 w-3" />
                    </router-link>
                  </div>
                </div>
              </div>
            </section>
          </div>

        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onUnmounted, watch, computed } from 'vue';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '@/firebaseConfig';
import { useUserStore } from '@/stores/userStore';
import { 
  ClipboardDocumentListIcon, 
  DocumentTextIcon, 
  ChevronRightIcon,
  MagnifyingGlassIcon,
  ArrowUturnLeftIcon,
  FolderIcon,
  ShieldCheckIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const loading = ref(true);
const error = ref(null);
const documents = ref([]);
const searchQuery = ref('');
const selectedCategory = ref('all');
let unsubscribe = null;

// Dynamically extracts available categories directly from loaded database records [1.1.4]
const availableCategories = computed(() => {
  const cats = new Set();
  documents.value.forEach(doc => {
    if (doc.type) {
      cats.add(doc.type);
    }
  });
  return Array.from(cats).sort();
});

const groupedDocuments = computed(() => {
  const queryText = searchQuery.value.toLowerCase().trim();
  const groups = {};

  documents.value.forEach(doc => {
    // Dual-Filter search handshake: Match either title OR reference [1.1.4]
    const matchesTitle = doc.title && doc.title.toLowerCase().includes(queryText);
    const matchesReference = doc.reference && doc.reference.toLowerCase().includes(queryText);

    if (queryText && !matchesTitle && !matchesReference) {
      return; // Skip if search query exists and neither match
    }

    const category = doc.type || 'uncategorized';
    
    // Category dropdown filtering [1.1.4]
    if (selectedCategory.value !== 'all' && category !== selectedCategory.value) {
      return;
    }

    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(doc);
  });

  const sortedGroups = {};
  Object.keys(groups).sort().forEach(key => {
    sortedGroups[key] = groups[key];
  });
  
  return sortedGroups;
});

const formatDate = (timestamp) => {
  if (!timestamp || !timestamp.toDate) return 'N/A';
  return timestamp.toDate().toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

const setupFirestoreListener = (clientId) => {
  if (unsubscribe) unsubscribe();
  loading.value = true;
  error.value = null;

  try {
    const documentsRef = collection(db, 'clients', clientId, 'documents');
    const q = query(
      documentsRef,
      where('directorSignature', '!=', null),
      orderBy('directorSignature'),
      orderBy('signedAt', 'desc')
    );

    unsubscribe = onSnapshot(q, (snapshot) => {
      documents.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      loading.value = false;
    }, (err) => {
      console.error("Error fetching published documents:", err);
      error.value = "An error occurred while loading documents.";
      loading.value = false;
    });
  } catch (err) {
    console.error("Error setting up Firestore listener:", err);
    error.value = "Could not initialize document listener.";
    loading.value = false;
  }
};

watch(() => userStore.isUserProfileLoading, (isLoading) => {
  if (!isLoading && userStore.clientId) {
    setupFirestoreListener(userStore.clientId);
  } else if (!isLoading && !userStore.clientId) {
    error.value = "Could not verify your client ID.";
    loading.value = false;
  }
}, { immediate: true });

onUnmounted(() => {
  if (unsubscribe) {
    unsubscribe();
  }
});
</script>

<style scoped>
.filter-input-dark { @apply w-full bg-[#1E293B] border border-white/10 rounded-xl px-5 py-3 text-sm font-bold text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all placeholder-slate-500 shadow-inner; }
.filter-select-dark {
  @apply w-full bg-[#1E293B] border border-white/10 rounded-xl px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all cursor-pointer appearance-none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2338E3B0'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1rem;
}
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
</style>